/**
 * Sticky RMA detail toolbar: primary actions, overflow menu, and modal resolver entry (no per-action type branching here).
 */
import { observer } from "mobx-react";
import IconCircleRestocked from "../../../../../components/icons/icon-circle-restocked/icon-circle-restocked";
import RmaPageStore, { IRmaAction } from "../../stores/rma-page-store";
import { useState } from "react";
import { Button } from "../../../../../components/inputs/button/button";
import { BUTTON_VARIANTS } from "../../../../../components/inputs/button-variants";
import MoreActionsMenu from "./more-actions-menu/more-actions-menu";
import IconPolicyGroupReturn from "../../../../../components/icons/icon-policy-group-return/icon-policy-group-return";

import "./rma-actions-toolbar.scss";
import currentPortalStore from "../../../../../stores/current-portal-store";
import currentAccountStore from "../../../../accounts/stores/current-account-store";
import { IRMAEmailLog } from "../../rma-types";
import ResendStatusModal from "../resend-status-email/resend-status-email-modal/resend-status-email-modal";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import dashboardTranslationsStore from "../../../../../dashboard-translation/stores/dashboard-translations-store";
import BaseLayoutStickyToolbar
    from "../../../../../components/layouts/base-page-layout/base-layout-sticky-toolbar/base-layout-sticky-toolbar";
import RmaToolbarActionModalResolver from "./rma-toolbar-action-modal-resolver";
import ReleaseExchangeToolbarButton from "./release-exchange-toolbar-button";
import rmsMixpanelStore from "../../../../../stores/rms-mixpanel-store";
import RmaNextPrimaryActionButton from "./rma-next-primary-action-button";
import RmaValidateAllToolbarButton from "./rma-validate-all-toolbar-button";

interface IProps {
    rma: RmaPageStore;
}

export const RmaActionsToolbar: React.FC<IProps> = observer(({ rma }) => {
    const currentRmaAction: IRmaAction | null = rma.rmaActionsToolbarStore().getCurrentAction();
    const setCurrentRmaAction = rma.rmaActionsToolbarStore().setCurrentAction;

    const nextAction = rma.nextAction();
    const releaseExchangeAction = rma.releaseExchangeAction();
    const restockAction = rma.restockAction();
    const refundAction = rma.refundAction();
    const validateAllAction = rma.validateAllAction();

    const { getTranslation } = useDashboardTranslation();
    const scope = "dashboard-returns-rma-card";

    const areActionsDisabled = currentPortalStore.isLoading || currentAccountStore.isLoading || !rma.orderStore || !rma.customerCaseStore?.customer;

    const dismissCurrentRmaAction = () => setCurrentRmaAction(null);

    const onRefundClick = () => {
        rmsMixpanelStore.trackEvent("d&_refund_credit_button");
        setCurrentRmaAction(refundAction);
        refundAction?.store.submitAction();
    };

    const handleRestockClick = () => {
        setCurrentRmaAction(restockAction);
    };

    const [currentEmailLog, setCurrentEmailLog] = useState<IRMAEmailLog | null>(null);

    return (
        <>
            <BaseLayoutStickyToolbar>
                {refundAction && rma.isRefundAllowed() &&
                    <Button
                        variant={BUTTON_VARIANTS.PrimaryAccent}
                        leftIcon={() => <IconPolicyGroupReturn color={"var(--color-neutral-1-white)"} />}
                        text={getTranslation(refundAction.buttonTextTranslationKey, scope)}
                        onClick={() => onRefundClick()}
                        hasLoader={true}
                        disabled={areActionsDisabled}
                        isLoading={rma.refundCalculatorStore?.isLoading}
                    />
                }

                {restockAction &&
                    <Button
                        variant={BUTTON_VARIANTS.PrimaryAccent}
                        leftIcon={() => <IconCircleRestocked color="var(--color-neutral-1-white)" />}
                        text={getTranslation(restockAction.name, scope)}
                        onClick={handleRestockClick}
                        disabled={restockAction.disabled || areActionsDisabled}
                    />
                }

                {releaseExchangeAction &&
                    <ReleaseExchangeToolbarButton
                        releaseExchangeAction={releaseExchangeAction}
                        label={getTranslation(releaseExchangeAction.name, scope)}
                        disabled={releaseExchangeAction.disabled || areActionsDisabled}
                        isLoading={rma.isReleasingExchnage}
                    />
                }

                {(validateAllAction && rma.isValidateAllEnabled && !dashboardTranslationsStore.isLoading) &&
                    <RmaValidateAllToolbarButton
                        validateAllAction={validateAllAction}
                        disabled={areActionsDisabled}
                        label={getTranslation(validateAllAction.name, scope)}
                        setCurrentRmaAction={setCurrentRmaAction}
                    />
                }

                <div className="rma-actions-toolbar-right">
                    <MoreActionsMenu rma={rma} setCurrentRmaAction={setCurrentRmaAction} setCurrentEmailLog={setCurrentEmailLog} />
                    {currentEmailLog && <ResendStatusModal rma={rma} emailLog={currentEmailLog} onCloseClick={() => setCurrentEmailLog(null)} />}

                    {!!nextAction &&
                        <RmaNextPrimaryActionButton
                            nextAction={nextAction}
                            rma={rma}
                            disabled={areActionsDisabled}
                            label={getTranslation(nextAction.name, scope)}
                            setCurrentRmaAction={setCurrentRmaAction}
                        />
                    }
                </div>
            </BaseLayoutStickyToolbar>

            {!!currentRmaAction &&
                <RmaToolbarActionModalResolver
                    rma={rma}
                    currentRmaAction={currentRmaAction}
                    onCloseClick={dismissCurrentRmaAction}
                />}
        </>
    );
});

export default RmaActionsToolbar;
